<template>
  <div class="consent-download-container">
    <Combobox
      v-if="!isUsForm"
      v-model="consentSelectedLanguage"
      :label="t('Language')"
      :items="availableLanguages"
      hide-details
    />
    <div v-if="patientControl">
      <span> {{ t("OrderInfo_patient_consent" + props.translationKeySuffix) }}: </span>
      <Button
        class="ml-3 bg-primary text-white"
        @click="() => downloadConsent(consentTypes.patient)"
      >
        {{ t("Download") }}
      </Button>
    </div>
    <div v-if="parent1Control">
      <span> {{ t("OrderInfo_parent1_consent" + props.translationKeySuffix) }}: </span>
      <Button
        class="ml-3 bg-primary text-white"
        @click="() => downloadConsent(consentTypes.parent1)"
      >
        {{ t("Download") }}
      </Button>
    </div>
    <div v-if="parent2Control">
      <span> {{ t("OrderInfo_parent2_consent" + props.translationKeySuffix) }}: </span>
      <Button
        class="ml-3 bg-primary text-white"
        @click="() => downloadConsent(consentTypes.parent2)"
      >
        {{ t("Download") }}
      </Button>
    </div>

    <div
      class="mt-6"
      v-html="
        t(
          'OrderInfo_consent_is_signed' + props.translationKeySuffix
        )
      "
    />
  </div>
</template>

<script setup lang="ts">
import {ref, computed} from 'vue'
import Button from '@/components/input-components/Button.vue'
import Combobox from '@/components/input-components/Combobox.vue'
import {useOrdersStore} from '@/stores/orders'
import {
  CONSENT_FORM,
  FORM_CONTROLS,
  FORM_FLAGS,
  ORDER_INFO_PAGE_OPTIONS,
  ORDER_INFO_PAGE_SUFFIXES
} from "@/utils/constants"
import {useI18n} from 'vue-i18n'
import {Order} from "@/entities/order"
import {FormControl} from '@/entities/form-control'
import {useControlRenderer} from '@/composables/useControlRenderer';

const {showTab} = useControlRenderer();
const {t} = useI18n()

const props = defineProps({
  translationKeySuffix: {
    type: String,
    required: false,
    default: ''
  }
});

const consentSelectedLanguage = ref(CONSENT_FORM.defaultLanguage)
const availableLanguages = CONSENT_FORM.availableLanguages
const consentTypes = CONSENT_FORM.types

const ordersStore = useOrdersStore()
const order = computed<Order>(() => ordersStore.getOrder)

// Controls
const patientControl = computed(() =>
  getControlByTypeAndFlag(consentTypes.patient, FORM_FLAGS.isPatientConsent)
);

const parent1Control = computed(() =>
  getControlByTypeAndFlag(consentTypes.parent1, FORM_FLAGS.isParentConsent)
);

const parent2Control = computed(() =>
  getControlByTypeAndFlag(consentTypes.parent2, FORM_FLAGS.isParentConsent)
);

function getControlByTypeAndFlag(type: string, flag: string): FormControl | undefined {
  // 1. Get all controls with the specified name.
  const controls = ordersStore.getOrderFormControlsByName(
    `${type}${CONSENT_FORM.downloadBtnNameSuffix}`
  );

  if (!controls.length) return undefined;

  // 2. For the US form, look in any tab that is currently displayed.
  if (isUsForm.value) {
    const allTabs = order.value.form?.tabs || [];
    for (const tab of allTabs) {
      if (showTab(tab)) {
        const found = controls.find(c => tab.controls.includes(c.uniqueId));
        if (found) return found;
      }
    }
    return undefined;
  }

  // 3. For the other forms, filter the tabs based on the flag and check `showTab`.
  const tabs = ordersStore.getOrderFormTabsByFlag(flag);
  if (!tabs) return undefined;

  for (const tab of tabs) {
    if (showTab(tab)) {
      const found = controls.find(c => tab.controls.includes(c.uniqueId));
      if (found) return found;
    }
  }
  return undefined;
}

const isUsForm = computed(() =>
  props.translationKeySuffix === ORDER_INFO_PAGE_SUFFIXES.us_form ||
  ordersStore.getOrderInfoPage === ORDER_INFO_PAGE_OPTIONS.us_form
)

function getControlByType(type: string): FormControl | undefined {
  switch (type) {
    case consentTypes.patient:
      return patientControl.value
    case consentTypes.parent1:
      return parent1Control.value
    case consentTypes.parent2:
      return parent2Control.value
    default:
      return undefined
  }
}

async function downloadConsent(type: string) {
  const control = getControlByType(type)
  let template = CONSENT_FORM.defaultTemplate

  if (control) {
    const formTab = ordersStore.getOrderFormTabByControlId(control.uniqueId)
    if (formTab?.template) {
      template = formTab.template
    }
  }

  const payload = {
    orderId: order.value.orderId,
    type,
    data: {
      language: consentSelectedLanguage.value,
      formData: order.value.form,
    },
    template,
  }

  await ordersStore.fetchOrderConsent(payload)
}
</script>

<style lang="scss" scoped>
.consent-download-container {
  max-width: 25rem;

  > div {
    margin-top: 1rem;
    justify-content: space-between;
    align-items: center;
    display: flex
  }
}
</style>
